import { useEffect, useMemo, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { LargeTitleHeader } from '@/components/common';
import { useAuth } from '@/lib/hooks/useAuth';
import {
  getCategories, getTemplates, getSnippets, getContacts, deleteTemplate, sendBatch,
  getSuppression, addSuppression, removeSuppression, updateSnippet,
  type OutreachCategory, type OutreachTemplate, type OutreachContactListItem, type SuppressionEntry,
} from '@/lib/api/outreach';
import { ComposeDrawer } from '@/components/outreach/ComposeDrawer';
import { AiTemplateModal } from '@/components/outreach/AiTemplateModal';
import { TemplateEditModal } from '@/components/outreach/TemplateEditModal';
import { useToast } from '@/lib/hooks/useToast';

const STATUS_OPTS = ['all', 'new', 'drafted', 'sent', 'replied', 'declined', 'bounced', 'customer'];
const statusColor: Record<string, string> = {
  new: 'bg-gray-100 text-gray-600', drafted: 'bg-blue-100 text-blue-700', sent: 'bg-amber-100 text-amber-700',
  replied: 'bg-green-100 text-green-700', declined: 'bg-red-100 text-red-700', bounced: 'bg-red-100 text-red-700',
  customer: 'bg-emerald-100 text-emerald-700',
};
const NAME_KEY = 'outreach.senderName';

// Inline editor for a reusable snippet ({{CREW}}/{{REFS}}/{{CLOSE}}).
function SnippetEditor({ skey, body, onSaved }: { skey: string; body: string; onSaved: (b: string) => void }) {
  const { toast } = useToast();
  const [value, setValue] = useState(body);
  const [saving, setSaving] = useState(false);
  const dirty = value !== body;
  const save = async () => {
    setSaving(true);
    try { await updateSnippet(skey, value); onSaved(value); toast.success(`{{${skey}}} updated`); }
    catch { toast.error('Failed to save snippet'); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-mono font-semibold text-primary">{`{{${skey}}}`}</span>
        {dirty && (
          <button onClick={save} disabled={saving} className="text-xs text-primary font-semibold hover:underline disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
      <textarea value={value} onChange={e => setValue(e.target.value)} rows={3}
        className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm leading-relaxed bg-white" />
    </div>
  );
}

export function OutreachPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState<'contacts' | 'templates' | 'suppression'>('contacts');
  const [categories, setCategories] = useState<OutreachCategory[]>([]);
  const [templates, setTemplates] = useState<OutreachTemplate[]>([]);
  const [snippets, setSnippets] = useState<Record<string, string>>({});
  const [contacts, setContacts] = useState<OutreachContactListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [composeId, setComposeId] = useState<number | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<OutreachTemplate | null>(null);
  const [batchSending, setBatchSending] = useState(false);
  // Suppression tab
  const [suppression, setSuppression] = useState<SuppressionEntry[]>([]);
  const [suppLoading, setSuppLoading] = useState(false);
  const [suppNew, setSuppNew] = useState('');
  const [senderName, setSenderName] = useState(localStorage.getItem(NAME_KEY) || user?.username || '');

  useEffect(() => {
    Promise.all([getCategories(), getTemplates(), getSnippets()])
      .then(([cats, tmpls, snips]) => { setCategories(cats); setTemplates(tmpls); setSnippets(snips); })
      .catch(() => toast.error('Failed to load outreach data'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContacts = () => {
    setLoading(true);
    getContacts({ category, status, q })
      .then(setContacts)
      .catch(() => toast.error('Failed to load contacts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(loadContacts, q ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, status, q]);

  const eligible = useMemo(
    () => contacts.filter(c => c.email && c.email.includes('@') && c.status !== 'sent'),
    [contacts],
  );

  const runBatch = async () => {
    const ids = eligible.map(c => c.id);
    if (ids.length === 0) { toast.info('No un-emailed contacts with an address in this view'); return; }
    if (!senderName.trim()) { toast.error('Set "Your name" first (used in the emails)'); return; }
    if (!window.confirm(`Send the saved draft to ${Math.min(ids.length, 40)} contact${ids.length === 1 ? '' : 's'} as OchrePoint?${ids.length > 40 ? ' (capped at 40 per batch)' : ''}\n\nOnly contacts with a draft + email that haven't been emailed will receive it.`)) return;
    setBatchSending(true);
    try {
      const r = await sendBatch(ids, senderName, user?.email || undefined);
      toast.success(`${r.sent} sent${r.skipped.length ? `, ${r.skipped.length} skipped` : ''}${r.capped ? ` (capped at ${r.capped})` : ''}`);
      loadContacts();
    } catch {
      toast.error('Batch send failed');
    } finally {
      setBatchSending(false);
    }
  };

  const loadSuppression = () => {
    setSuppLoading(true);
    getSuppression().then(setSuppression).catch(() => toast.error('Failed to load suppression list')).finally(() => setSuppLoading(false));
  };
  useEffect(() => {
    if (tab === 'suppression') loadSuppression();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const addSupp = async () => {
    const email = suppNew.trim().toLowerCase();
    if (!email.includes('@')) { toast.error('Enter a valid email'); return; }
    try { await addSuppression(email); setSuppNew(''); toast.success(`${email} suppressed`); loadSuppression(); }
    catch { toast.error('Failed to add'); }
  };
  const removeSupp = async (email: string) => {
    if (!window.confirm(`Remove ${email} from the suppression list? They will become emailable again.`)) return;
    try { await removeSuppression(email); loadSuppression(); toast.success('Removed'); }
    catch { toast.error('Failed to remove'); }
  };

  const saveName = (v: string) => { setSenderName(v); localStorage.setItem(NAME_KEY, v); };
  const refreshTemplates = () => getTemplates().then(setTemplates).catch(() => {});

  const counts = useMemo(() => {
    const by: Record<string, number> = {};
    for (const c of contacts) by[c.status] = (by[c.status] || 0) + 1;
    return by;
  }, [contacts]);

  return (
    <MainLayout>
      {/* CR #183: MainLayout's <main> is a flex column — multiple direct children
          get flex-shrunk (the contacts table collapsed to one visible row). All
          page content must be ONE child so the page scrolls normally. */}
      <div className="flex flex-col gap-4 md:gap-6">
      <LargeTitleHeader
        title="Outreach"
        subtitle="B2B mailing — contacts, templates & AI drafting"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 h-9 px-2 rounded-lg bg-white border border-gray-200" title="Used for [Your Name] in emails">
              <span className="material-symbols-outlined text-base text-gray-400">badge</span>
              <input value={senderName} onChange={e => saveName(e.target.value)} placeholder="Your name"
                className="text-sm outline-none w-28" />
            </label>
            <button onClick={() => setAiOpen(true)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90">
              <span className="material-symbols-outlined text-base">auto_awesome</span>
              <span className="hidden sm:inline">New template (AI)</span>
            </button>
          </div>
        }
      />

      <div className="flex gap-1 border-b border-gray-200 mb-4">
        {(['contacts', 'templates', 'suppression'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === t ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t === 'contacts' ? `Contacts (${contacts.length})` : t === 'templates' ? `Templates (${templates.length})` : 'Do not email'}
          </button>
        ))}
      </div>

      {tab === 'contacts' ? (
        <>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {categories.map(cat => (
              <button key={cat.id} onClick={() => setCategory(cat.id)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium ${category === cat.id ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {cat.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="flex items-center gap-1.5 h-9 px-2 rounded-lg bg-white border border-gray-200 flex-1 min-w-[12rem]">
              <span className="material-symbols-outlined text-base text-gray-400">search</span>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, email, specialty…"
                className="text-sm outline-none flex-1" />
            </div>
            <select value={status} onChange={e => setStatus(e.target.value)} className="h-9 px-2 rounded-lg border border-gray-300 text-sm">
              {STATUS_OPTS.map(s => <option key={s} value={s}>{s === 'all' ? 'All statuses' : s}{s !== 'all' && counts[s] ? ` (${counts[s]})` : ''}</option>)}
            </select>
            <button
              onClick={runBatch}
              disabled={batchSending || eligible.length === 0}
              title="Send each contact's saved draft, throttled, skipping suppressed/already-sent (max 40 per batch)"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white text-gray-700 border border-gray-300 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
            >
              <span className={`material-symbols-outlined text-base ${batchSending ? 'animate-spin' : ''}`}>{batchSending ? 'progress_activity' : 'forward_to_inbox'}</span>
              <span className="hidden sm:inline">{batchSending ? 'Sending…' : `Email ${eligible.length} shown`}</span>
            </button>
          </div>

          {loading ? (
            <div className="space-y-2">{[0, 1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />)}</div>
          ) : contacts.length === 0 ? (
            <p className="text-gray-500 text-sm py-8 text-center">No contacts match.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 bg-gray-50 border-b border-gray-100">
                    <th className="px-3 py-2 font-medium">Business</th>
                    <th className="px-3 py-2 font-medium">Category</th>
                    <th className="px-3 py-2 font-medium">Email</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Last contacted</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map(c => (
                    <tr key={c.id} onClick={() => setComposeId(c.id)} className="border-b border-gray-50 hover:bg-primary/5 cursor-pointer">
                      <td className="px-3 py-2">
                        <span className="font-medium text-gray-900">{c.name}</span>
                        {!!c.verified && <span className="material-symbols-outlined text-sm text-green-600 align-middle ml-1" title="Verified">verified</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-500">{c.category}</td>
                      <td className="px-3 py-2 text-gray-500 max-w-[16rem] truncate">{c.email || <span className="text-red-400">none</span>}</td>
                      <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${statusColor[c.status] || ''}`}>{c.status}</span></td>
                      <td className="px-3 py-2 text-gray-400 text-xs">{c.last_contacted_at ? new Date(c.last_contacted_at).toLocaleDateString('en-CA') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : tab === 'suppression' ? (
        <div className="space-y-3 max-w-2xl">
          <p className="text-xs text-gray-500">
            Addresses here are never emailed — added automatically when someone clicks the unsubscribe
            link in an outreach email, or manually below.
          </p>
          <div className="flex items-center gap-2">
            <input
              value={suppNew}
              onChange={e => setSuppNew(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addSupp(); }}
              placeholder="email@to-suppress.com"
              className="h-9 px-3 rounded-lg border border-gray-300 text-sm flex-1 font-mono"
            />
            <button onClick={addSupp} className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90">
              Suppress
            </button>
          </div>
          {suppLoading ? (
            <div className="h-24 animate-pulse bg-gray-100 rounded-xl" />
          ) : suppression.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No suppressed addresses — nobody has unsubscribed.</p>
          ) : (
            <div className="rounded-xl border border-gray-200 divide-y divide-gray-50">
              {suppression.map(s => (
                <div key={s.email} className="flex items-center gap-3 px-3 py-2">
                  <span className="material-symbols-outlined text-red-400 text-base">block</span>
                  <span className="text-sm font-mono text-gray-800 flex-1 truncate">{s.email}</span>
                  <span className="text-xs text-gray-400 capitalize">{s.reason}{s.source ? ` · ${s.source}` : ''}</span>
                  <span className="text-xs text-gray-400">{new Date(s.created_at).toLocaleDateString('en-CA')}</span>
                  <button onClick={() => removeSupp(s.email)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600" title="Remove from suppression">
                    <span className="material-symbols-outlined text-base">delete</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {/* Reusable snippets — expanded into {{TOKENS}} in every template/send */}
          <details className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
            <summary className="cursor-pointer text-sm font-semibold text-gray-800 select-none">
              Snippets <span className="text-xs text-gray-400 font-normal">— reusable blocks: {Object.keys(snippets).map(k => `{{${k}}}`).join(' · ')}</span>
            </summary>
            <div className="mt-3 space-y-3">
              {Object.entries(snippets).map(([key, body]) => (
                <SnippetEditor key={key} skey={key} body={body}
                  onSaved={(nb) => setSnippets(s => ({ ...s, [key]: nb }))} />
              ))}
            </div>
          </details>
          <div className="flex justify-end">
            <button onClick={() => setEditTemplate({ id: '', title: '' })}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white text-gray-700 border border-gray-300 text-sm font-semibold hover:bg-gray-50">
              <span className="material-symbols-outlined text-base">add</span> New template
            </button>
          </div>
          {templates.map(t => (
            <div key={t.id} className="rounded-xl border border-gray-200 p-3 hover:border-primary/40">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 cursor-pointer flex-1" onClick={() => setEditTemplate(t)}>
                  <p className="font-semibold text-gray-900">{t.title} {t.category && <span className="text-xs text-gray-400 font-normal">· {t.category}</span>}</p>
                  {t.subject && <p className="text-xs text-gray-500 mt-0.5 truncate">{t.subject}</p>}
                  {t.body && <p className="text-xs text-gray-400 mt-1 line-clamp-2 whitespace-pre-wrap">{t.body.slice(0, 220)}</p>}
                </div>
                <div className="shrink-0 flex items-center gap-1">
                  <button onClick={() => setEditTemplate(t)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-primary" title="Edit">
                    <span className="material-symbols-outlined text-lg">edit</span>
                  </button>
                  <button
                    onClick={async () => { if (window.confirm(`Delete template "${t.title}"?`)) { await deleteTemplate(t.id); refreshTemplates(); } }}
                    className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600" title="Delete">
                    <span className="material-symbols-outlined text-lg">delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {composeId != null && (
        <ComposeDrawer
          contactId={composeId}
          snippets={snippets}
          templates={templates}
          senderName={senderName}
          replyTo={user?.email || ''}
          onClose={() => setComposeId(null)}
          onChanged={loadContacts}
        />
      )}
      {aiOpen && (
        <AiTemplateModal categories={categories} onClose={() => setAiOpen(false)} onSaved={refreshTemplates} />
      )}
      {editTemplate && (
        <TemplateEditModal template={editTemplate} categories={categories} onClose={() => setEditTemplate(null)} onSaved={refreshTemplates} />
      )}
    </MainLayout>
  );
}
